import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import router from '@/router'
import httpClient from '@/services/httpClient'
import AuthService from '@/services/authServices'
import {
  getAuthToken,
  hasAdminHubToken,
  ADMIN_HUB_TOKEN_KEY,
  ADMIN_HUB_CUSTOMER_NAME_KEY,
  ADMIN_HUB_USER_KEY,
  ADMIN_HUB_TOKEN_PARAM,
  ADMIN_HUB_NAME_PARAM,
} from '@/utils/authToken'
import { releaseTenantForTab, TENANT_PARAM } from '@/utils/tenant'
import { clearSnapshots } from '@/utils/snapshot'
import type {
  LoginPayload,
  LoginResponse,
  AuthUser,
  Customer,
  UpdateProfilePayload,
} from '@/services/authServices'

/**
 * The persisted user: an admin-hub session (sessionStorage) wins over a normal
 * login (localStorage), mirroring `getAuthToken()`.
 */
function getStoredUser(): AuthUser | null {
  const raw = sessionStorage.getItem(ADMIN_HUB_USER_KEY) ?? localStorage.getItem('authUser')
  return raw ? (JSON.parse(raw) as AuthUser) : null
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const resetEmail = ref<string>()
  const token = ref<string | null>(getAuthToken())
  const tokenType = ref<string | null>(localStorage.getItem('authTokenType'))
  const tokenExpiredAt = ref<string | null>(localStorage.getItem('authTokenExpiredAt'))
  const user = ref<AuthUser | null>(getStoredUser())
  // Admin-hub impersonation: a customer (not a user) was logged in from the
  // separate admin hub app via the `customer_token` URL param.
  const isAdminHubSession = ref<boolean>(hasAdminHubToken())
  const adminHubCustomerName = ref<string | null>(
    sessionStorage.getItem(ADMIN_HUB_CUSTOMER_NAME_KEY),
  )
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value)
  const userFullName = computed(() => {
    if (!user.value) return ''
    const parts = [user.value.firstName, user.value.middleName, user.value.lastName].filter(Boolean)
    return parts.join(' ')
  })

  /** The customer the session is currently acting as. */
  const activeCustomer = computed<Customer | null>(() => user.value?.customer ?? null)

  /**
   * Every customer this user may act as, sorted by name so the switcher list is
   * stable. Accounts assigned to a single customer get no `customers` array from
   * the backend, so fall back to their one customer and callers can treat the
   * list uniformly.
   */
  const assignedCustomers = computed<Customer[]>(() => {
    const list = user.value?.customers
    const customers =
      Array.isArray(list) && list.length > 0
        ? list
        : user.value?.customer
          ? [user.value.customer]
          : []
    return [...customers].sort((a, b) => a.name.localeCompare(b.name))
  })

  /**
   * Whether to offer the switcher at all. Users with one customer see the
   * interface exactly as before. An admin-hub impersonation is scoped to the
   * single customer it was handed off for and never switches.
   */
  const canSwitchCustomer = computed(
    () => !isAdminHubSession.value && assignedCustomers.value.length > 1,
  )

  // Actions
  async function login(payload: LoginPayload): Promise<LoginResponse> {
    const { data } = await httpClient.post<LoginResponse>('/auth/login', payload)

    if (data.user?.user_type !== 'customer_user') {
      throw new Error('NOT_CUSTOMER')
    }

    // Update state
    token.value = data.token
    tokenType.value = data.token_type
    tokenExpiredAt.value = data.token_expired_at

    // Persist to localStorage
    localStorage.setItem('authToken', data.token)
    localStorage.setItem('authTokenType', data.token_type)
    localStorage.setItem('authTokenExpiredAt', data.token_expired_at)

    return data
  }

  /** Clear all auth state and persisted tokens, without calling the API. */
  function clearSession(): void {
    token.value = null
    tokenType.value = null
    tokenExpiredAt.value = null
    user.value = null
    isAdminHubSession.value = false
    adminHubCustomerName.value = null

    localStorage.removeItem('authToken')
    localStorage.removeItem('authTokenType')
    localStorage.removeItem('authTokenExpiredAt')
    localStorage.removeItem('authUser')
    // The lists kept for a quick opening belong to this user
    clearSnapshots()

    // Admin-hub impersonation lives in sessionStorage — clear it too.
    sessionStorage.removeItem(ADMIN_HUB_TOKEN_KEY)
    sessionStorage.removeItem(ADMIN_HUB_CUSTOMER_NAME_KEY)
    sessionStorage.removeItem(ADMIN_HUB_USER_KEY)
  }

  /**
   * Log a customer in via the admin-hub handoff. The `customer_token` from the
   * admin hub isn't a valid app token on its own — we exchange it through the
   * regular login endpoint (passed as `token` in the body) for a real app
   * session token. Any pre-existing session is wiped first, and the returned
   * token is persisted to sessionStorage so the browser garbage-collects it
   * when the tab/window closes.
   *
   * We don't rely on the response to denote a customer; we set
   * `isAdminHubSession` ourselves because we arrived via the customer_token flow.
   */
  async function loginWithAdminHubToken(customerToken: string): Promise<LoginResponse> {
    // Override/clear anything already stored (stale user or admin-hub session).
    clearSession()

    const { data } = await httpClient.post<LoginResponse>('/auth/login', {
      token: customerToken,
    })

    sessionStorage.setItem(ADMIN_HUB_TOKEN_KEY, data.token)
    token.value = data.token
    tokenType.value = data.token_type
    tokenExpiredAt.value = data.token_expired_at
    isAdminHubSession.value = true

    if (data.user) {
      user.value = data.user
      sessionStorage.setItem(ADMIN_HUB_USER_KEY, JSON.stringify(data.user))
      setAdminHubCustomerName(data.user?.customer?.name ?? null)
    }

    return data
  }

  /**
   * Detect an admin-hub handoff in the current URL (`?customer_token=...`),
   * exchange it for a session, and strip the param from the address bar.
   * Returns true if a handoff token was present. Call this once, before the
   * first route resolves.
   */
  async function ingestAdminHubTokenFromUrl(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search)
    const handoffToken = params.get(ADMIN_HUB_TOKEN_PARAM)
    if (!handoffToken) return false

    // The customer (company) name isn't in the /auth/login response, so the
    // admin hub passes it alongside the token.
    const customerName = params.get(ADMIN_HUB_NAME_PARAM)

    try {
      await loginWithAdminHubToken(handoffToken)
      if (customerName) setAdminHubCustomerName(customerName)
    } finally {
      // Strip the params from the URL regardless of outcome, so they aren't
      // bookmarked/shared/logged.
      params.delete(ADMIN_HUB_TOKEN_PARAM)
      params.delete(ADMIN_HUB_NAME_PARAM)
      params.delete(TENANT_PARAM)
      const query = params.toString()
      const cleanUrl = window.location.pathname + (query ? `?${query}` : '') + window.location.hash
      window.history.replaceState({}, '', cleanUrl)
    }

    return true
  }

  /** Persist the impersonated customer's display name for the banner. */
  function setAdminHubCustomerName(name: string | null): void {
    adminHubCustomerName.value = name
    if (name) {
      sessionStorage.setItem(ADMIN_HUB_CUSTOMER_NAME_KEY, name)
    } else {
      sessionStorage.removeItem(ADMIN_HUB_CUSTOMER_NAME_KEY)
    }
  }

  async function logout(): Promise<void> {
    try {
      await httpClient.delete('/auth/logout', {
        headers: {
          Authorization: `Bearer ${token.value}`,
        },
      })
    } catch (error) {
      console.error('Logout API error:', error)
    }

    clearSession()

    // An AdminHub session's tab back on the browser's tenant: loaded anew at its backend
    if (releaseTenantForTab()) {
      window.location.assign(`${import.meta.env.BASE_URL}login`)
      return
    }

    // Redirect to login
    router.push('/login')
  }

  async function fetchCurrentUser() {
    loading.value = true
    error.value = null
    try {
      const response = await AuthService.getCurrentUser()
      user.value = response.data
      // Update localStorage
      localStorage.setItem('authUser', JSON.stringify(response.data))
      return response.data
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch user profile'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updateProfile(payload: UpdateProfilePayload) {
    loading.value = true
    error.value = null
    try {
      const response = await AuthService.updateProfile(payload)
      user.value = response.data
      // Update localStorage
      localStorage.setItem('authUser', JSON.stringify(response.data))
      return response.data
    } catch (err: any) {
      error.value = err.message || 'Failed to update profile'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Make another assigned customer the active one for this session on this
   * device. Persists the refreshed user so it survives the reload the caller
   * performs afterwards to clear every other store of the old company's data.
   */
  async function switchCustomer(customerId: number): Promise<AuthUser> {
    const { data } = await AuthService.switchCustomer(customerId)
    user.value = data
    localStorage.setItem('authUser', JSON.stringify(data))
    return data
  }

  function initializeAuth() {
    // Called on app mount to sync state with storage. The admin-hub token
    // (sessionStorage) wins over a normal login token (localStorage).
    token.value = getAuthToken()
    tokenType.value = localStorage.getItem('authTokenType')
    tokenExpiredAt.value = localStorage.getItem('authTokenExpiredAt')
    user.value = getStoredUser()
    isAdminHubSession.value = hasAdminHubToken()
    adminHubCustomerName.value = sessionStorage.getItem(ADMIN_HUB_CUSTOMER_NAME_KEY)
  }

  function clearError() {
    error.value = null
  }

  return {
    // State
    resetEmail,
    token,
    tokenType,
    tokenExpiredAt,
    user,
    isAdminHubSession,
    adminHubCustomerName,
    loading,
    error,
    // Getters
    isAuthenticated,
    userFullName,
    activeCustomer,
    assignedCustomers,
    canSwitchCustomer,
    // Actions
    login,
    logout,
    clearSession,
    loginWithAdminHubToken,
    ingestAdminHubTokenFromUrl,
    setAdminHubCustomerName,
    fetchCurrentUser,
    updateProfile,
    switchCustomer,
    initializeAuth,
    clearError,
  }
})
